import { getAdapter } from '@jbrowse/core/data_adapters/dataAdapterCache'
import { firstValueFrom, toArray } from 'rxjs'

import { getFeaturesThatPassMinorAlleleFrequencyFilter } from '../shared/minorAlleleFrequencyUtils'

import type { GetGenotypeMatrixArgs } from './types'
import type PluginManager from '@jbrowse/core/PluginManager'
import type { BaseFeatureDataAdapter } from '@jbrowse/core/data_adapters/BaseAdapter'

export async function getGenotypeMatrix({
  pluginManager,
  args,
}: {
  pluginManager: PluginManager
  args: GetGenotypeMatrixArgs
}) {
  const {
    sources,
    minorAlleleFrequencyFilter,
    regions,
    adapterConfig,
    sessionId,
  } = args
  const adapter = await getAdapter(pluginManager, sessionId, adapterConfig)
  const dataAdapter = adapter.dataAdapter as BaseFeatureDataAdapter

  const feats = await firstValueFrom(
    dataAdapter.getFeaturesInMultipleRegions(regions, args).pipe(toArray()),
  )

  const genotypeFactor = new Set<string>()
  const mafs = getFeaturesThatPassMinorAlleleFrequencyFilter(
    feats,
    minorAlleleFrequencyFilter,
  )

  for (const { alleleCounts } of mafs) {
    for (const alt of alleleCounts.keys()) {
      genotypeFactor.add(alt)
    }
  }

  const rows = {} as Record<string, number[]>
  for (const { feature } of mafs) {
    const genotypes = feature.get('genotypes') as Record<string, string>
    for (const { name } of sources) {
      if (!rows[name]) {
        rows[name] = []
      }
      const val = genotypes[name]!
      const alleles = val.split(/[/|]/)

      // Calculate '012' status of the allele for a VCF genotype matrix
      // Similar to vcftools --012 https://vcftools.github.io/man_latest.html
      // Note: could also consider https://www.rdocumentation.org/packages/fastcluster/versions/1.2.6/topics/hclust.vector for clustering true multi-dimensional allele count vectors
      let genotypeStatus = 0
      let nonRefCount = 0
      let uncalledCount = 0
      for (const l of alleles) {
        if (l === '.') {
          uncalledCount++
        } else if (l !== '0') {
          nonRefCount++
        }
      }

      if (uncalledCount === alleles.length) {
        genotypeStatus = -1 // All no-call
      } else if (nonRefCount === 0) {
        genotypeStatus = 0 // Homozygous reference
      } else if (nonRefCount === alleles.length) {
        genotypeStatus = 2 // Homozygous alternate
      } else {
        genotypeStatus = 1 // Heterozygous
      }

      rows[name].push(genotypeStatus)
    }
  }
  return rows
}
